import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, PriceBlock } from '@/components/ui';
import { cn } from '@/lib/utils';
import type {
  Printing,
  TcgCardSuggestion,
  TcgCardVariant,
} from '@/types';
import { formatPrice, toNumber } from '@/utils/format';
import { CatalogImage } from './CatalogImage';
import { catalogSaleCountLabel } from '../utils/stockDisplay';

const GRID_CARD_CLASS =
  'group grid h-full min-h-[13.5rem] cursor-pointer grid-cols-[100px_minmax(0,1fr)] overflow-hidden rounded-md border border-brand-700/15 bg-surface shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 sm:grid-cols-[120px_minmax(0,1fr)]';
const GRID_MEDIA_CLASS =
  'flex h-full min-h-[13.5rem] w-[100px] items-center justify-center overflow-hidden bg-brand-100 sm:w-[120px]';
const GRID_IMAGE_CLASS =
  'h-[92%] w-[92%] object-contain transition-transform group-hover:scale-[1.03]';

type Money = {
  price: number | string;
  currency: string;
  priceCop?: number | string | null;
  priceUsd?: number | string | null;
};

type Listing = Printing['listings'][number];

type OfferRow = {
  printing: Printing;
  listing: Listing;
};

type TcgCardSearchResultsProps = {
  printings: Printing[];
  suggestions?: TcgCardSuggestion[];
  query?: string;
  displayMode?: 'grid' | 'list';
  className?: string;
};

function normalizeKey(value: string | number | null | undefined): string {
  return String(value ?? '').trim().toLowerCase();
}

function getPrintingInfo(printing: Printing): string {
  return [printing.setCode, printing.setRarityCode || printing.setRarity, printing.setEdition]
    .filter(Boolean)
    .join(' - ');
}

function getVariantInfo(variant: TcgCardVariant | null): string {
  if (!variant) return '';
  return [variant.setCode, variant.setRarityCode || variant.setRarity, variant.setEdition]
    .filter(Boolean)
    .join(' - ');
}

function getPrintingDetailUrl(printing: Printing): string {
  const rarityValue = printing.setRarityCode || printing.setRarity || '';
  return `/yugioh/cards/${printing.card.id}?setCode=${encodeURIComponent(
    printing.setCode || '',
  )}&setRarityCode=${encodeURIComponent(rarityValue)}&setEdition=${encodeURIComponent(
    printing.setEdition || '',
  )}`;
}

function getSuggestionDetailUrl(
  suggestion: TcgCardSuggestion,
  variant: TcgCardVariant | null,
): string | null {
  if (!variant?.setCode) return null;
  const rarityValue = variant.setRarityCode || variant.setRarity || '';
  return `/yugioh/cards/${suggestion.card.id}?setCode=${encodeURIComponent(
    variant.setCode,
  )}&setRarityCode=${encodeURIComponent(rarityValue)}&setEdition=${encodeURIComponent(
    variant.setEdition || '',
  )}`;
}

function getListingCount(printing: Printing): number {
  return printing.aggregatedStock?.listingCount ?? printing.listings?.length ?? 0;
}

function getStock(printing: Printing): number {
  if (printing.aggregatedStock?.aggregatedStock != null) {
    return printing.aggregatedStock.aggregatedStock;
  }
  return printing.listings?.reduce((total, listing) => total + (listing.stock ?? 0), 0) ?? 0;
}

function getListingPrice(printing: Printing): Money | null {
  if (printing.fromPrice) {
    return {
      price: Number(printing.fromPrice.price),
      currency: printing.fromPrice.currency,
      priceCop: printing.fromPrice.priceCop,
      priceUsd: printing.fromPrice.priceUsd,
    };
  }

  const activePrices =
    printing.listings
      ?.filter((listing) => listing.stock > 0)
      .map((listing) => ({
        price: Number(listing.price),
        currency: listing.currency,
        priceCop: listing.priceCop,
        priceUsd: listing.priceUsd,
      }))
      .sort((a, b) => toNumber(a.price) - toNumber(b.price)) ?? [];

  return activePrices[0] ?? null;
}

function getMarketPriceFromPrinting(printing: Printing): Money | null {
  if (printing.setPriceLow != null) {
    return {
      price: Number(printing.setPriceLow),
      currency: printing.setPriceLowCurrency || printing.setPriceCurrency || 'COP',
      priceCop: printing.setPriceLowCurrency === 'COP' ? printing.setPriceLow : undefined,
      priceUsd: printing.setPriceLowUsd ?? (printing.setPriceLowCurrency === 'USD' ? printing.setPriceLow : undefined),
    };
  }
  if (printing.setPrice != null) {
    return {
      price: Number(printing.setPrice),
      currency: printing.setPriceCurrency || 'COP',
      priceCop: printing.setPriceCurrency === 'COP' ? printing.setPrice : undefined,
      priceUsd: printing.setPriceUsd ?? (printing.setPriceCurrency === 'USD' ? printing.setPrice : undefined),
    };
  }
  return null;
}

function getVariantMarketPrice(variant: TcgCardVariant | null): Money | null {
  if (!variant) return null;
  if (variant.setPriceLow != null) {
    return {
      price: Number(variant.setPriceLow),
      currency: variant.setPriceLowCurrency || variant.setPriceCurrency || 'COP',
      priceCop: variant.setPriceLowCurrency === 'COP' ? variant.setPriceLow : undefined,
      priceUsd: variant.setPriceLowUsd ?? (variant.setPriceLowCurrency === 'USD' ? variant.setPriceLow : undefined),
    };
  }
  if (variant.setPrice != null) {
    return {
      price: Number(variant.setPrice),
      currency: variant.setPriceCurrency || 'COP',
      priceCop: variant.setPriceCurrency === 'COP' ? variant.setPrice : undefined,
      priceUsd: variant.setPriceUsd ?? (variant.setPriceCurrency === 'USD' ? variant.setPrice : undefined),
    };
  }
  return null;
}

function formatUsdSecondary(value?: number | string | null): string | undefined {
  if (value == null) return undefined;
  return `USD ${formatPrice(value, 'USD').replace(/^USD\s+/, '')}`;
}

function formatDualPrice(price: Money | null): { amount: string; secondary?: string } | null {
  if (!price) return null;
  const currency = price.currency?.toUpperCase();
  const cop = currency === 'COP' ? price.price : price.priceCop;
  const usd = currency === 'USD' ? price.price : price.priceUsd;

  return {
    amount: cop != null ? formatPrice(cop, 'COP') : formatPrice(price.price, price.currency),
    secondary: formatUsdSecondary(usd),
  };
}

function selectBestVariant(suggestion: TcgCardSuggestion): TcgCardVariant | null {
  const variants = suggestion.variants ?? [];
  if (variants.length === 0) return null;

  return [...variants].sort((a, b) => {
    const aPrice = getVariantMarketPrice(a);
    const bPrice = getVariantMarketPrice(b);
    if (!aPrice && !bPrice) return 0;
    if (!aPrice) return 1;
    if (!bPrice) return -1;
    return toNumber(aPrice.price) - toNumber(bPrice.price);
  })[0];
}

function selectBestPrinting(printings: Printing[]): Printing | null {
  if (printings.length === 0) return null;

  return [...printings].sort((a, b) => {
    const aListingPrice = getListingPrice(a);
    const bListingPrice = getListingPrice(b);
    if (aListingPrice && bListingPrice) {
      return toNumber(aListingPrice.price) - toNumber(bListingPrice.price);
    }
    if (aListingPrice) return -1;
    if (bListingPrice) return 1;

    const aMarketPrice = getMarketPriceFromPrinting(a);
    const bMarketPrice = getMarketPriceFromPrinting(b);
    if (!aMarketPrice && !bMarketPrice) return 0;
    if (!aMarketPrice) return 1;
    if (!bMarketPrice) return -1;
    return toNumber(aMarketPrice.price) - toNumber(bMarketPrice.price);
  })[0];
}

function getOfferRows(printings: Printing[]): OfferRow[] {
  return printings
    .flatMap((printing) =>
      (printing.listings ?? [])
        .filter((listing) => listing.stock > 0)
        .map((listing) => ({ printing, listing })),
    )
    .sort((a, b) => {
      const priceDiff = toNumber(a.listing.price) - toNumber(b.listing.price);
      if (priceDiff !== 0) return priceDiff;
      return getPrintingInfo(a.printing).localeCompare(getPrintingInfo(b.printing));
    });
}

function getSuggestionCodeKeys(suggestion: TcgCardSuggestion): string[] {
  return Array.from(
    new Set(
      (suggestion.variants ?? [])
        .map((variant) => normalizeKey(variant.setCode))
        .filter(Boolean),
    ),
  );
}

function uniquePrintings(printings: Printing[]): Printing[] {
  const seen = new Set<string>();
  return printings.filter((printing) => {
    const key = [
      printing.card.id,
      printing.setCode ?? '',
      printing.setRarityCode ?? printing.setRarity ?? '',
      printing.setEdition ?? '',
      printing.id,
    ].join('|');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function OfferList({ offers }: { offers: OfferRow[] }): JSX.Element {
  const { t } = useTranslation('catalog');
  const navigate = useNavigate();

  return (
    <div className="mt-2 rounded-md border border-brand-700/10 bg-brand-100/60 p-1.5">
      <div className="mb-1.5 font-sans text-2xs font-semibold uppercase text-brand-700">
        {t('offers.associatedTitle')}
      </div>
      <div className="grid max-h-44 gap-1.5 overflow-auto pr-1 sm:grid-cols-2">
        {offers.map((offer) => (
          <button
            key={`${offer.printing.id}-${offer.listing.id}`}
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              navigate(getPrintingDetailUrl(offer.printing));
            }}
            className="rounded-md border border-brand-700/15 bg-surface p-2 text-left transition-colors hover:border-brand-400 hover:bg-white"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="line-clamp-1 font-sans text-xs font-semibold text-brand-950">
                  {getPrintingInfo(offer.printing) || offer.printing.card.name}
                </p>
                <p className="mt-0.5 line-clamp-1 font-sans text-xs text-brand-700">
                  {[
                    offer.listing.condition,
                    offer.listing.language,
                    offer.listing.printingVariant,
                  ]
                    .filter(Boolean)
                    .join(' / ')}
                </p>
                {offer.listing.seller?.displayName ? (
                  <p className="mt-0.5 line-clamp-1 font-sans text-xs text-brand-700">
                    {t('seller.soldBy', { seller: offer.listing.seller.displayName })}
                  </p>
                ) : null}
              </div>
              <div className="shrink-0 text-right font-sans">
                {(() => {
                  const priceDisplay = formatDualPrice(offer.listing);
                  return (
                    <>
                      <p className="text-xs font-semibold text-brand-950">
                        {priceDisplay?.amount ?? t('list.priceUnavailable')}
                      </p>
                      {priceDisplay?.secondary ? (
                        <p className="mt-0.5 text-2xs text-brand-700">
                          {priceDisplay.secondary}
                        </p>
                      ) : null}
                    </>
                  );
                })()}
                <p className="text-xs font-semibold text-brand-950">
                  {t('offers.availableUnits', { count: offer.listing.stock })}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function TcgCardSearchResults({
  printings,
  suggestions = [],
  query = '',
  displayMode = 'grid',
  className,
}: TcgCardSearchResultsProps): JSX.Element | null {
  const { t } = useTranslation('catalog');
  const navigate = useNavigate();

  const printingsByCard = useMemo(() => {
    const byId = new Map<string, Printing[]>();
    const byName = new Map<string, Printing[]>();
    const bySetCode = new Map<string, Printing[]>();

    for (const printing of printings) {
      const cardId = normalizeKey(printing.card.id);
      const cardName = normalizeKey(printing.card.name);
      const setCode = normalizeKey(printing.setCode);

      byId.set(cardId, [...(byId.get(cardId) ?? []), printing]);
      byName.set(cardName, [...(byName.get(cardName) ?? []), printing]);
      if (setCode) {
        bySetCode.set(setCode, [...(bySetCode.get(setCode) ?? []), printing]);
      }
    }

    return { byId, byName, bySetCode };
  }, [printings]);

  const suggestionRows = useMemo(
    () =>
      suggestions.map((suggestion) => {
        const codeMatches = getSuggestionCodeKeys(suggestion).flatMap(
          (setCode) => printingsByCard.bySetCode.get(setCode) ?? [],
        );
        const matchingPrintings = uniquePrintings([
          ...codeMatches,
          ...(printingsByCard.byId.get(normalizeKey(suggestion.card.id)) ?? []),
          ...(printingsByCard.byName.get(normalizeKey(suggestion.card.name)) ?? []),
        ]);
        const offers = getOfferRows(matchingPrintings);
        const bestPrinting = selectBestPrinting(matchingPrintings);
        const bestVariant = selectBestVariant(suggestion);
        const listingPrice = bestPrinting ? getListingPrice(bestPrinting) : null;
        const marketPrice = getVariantMarketPrice(bestVariant);
        const stockFromOffers = offers.reduce((total, offer) => total + offer.listing.stock, 0);
        const stock =
          stockFromOffers ||
          matchingPrintings.reduce((total, printing) => total + getStock(printing), 0);
        const listings = offers.length
          ? offers.length
          : matchingPrintings.reduce((total, printing) => total + getListingCount(printing), 0);

        return {
          suggestion,
          bestPrinting,
          bestVariant,
          displayPrice: offers[0]
            ? {
                price: offers[0].listing.price,
                currency: offers[0].listing.currency,
                priceCop: offers[0].listing.priceCop,
                priceUsd: offers[0].listing.priceUsd,
              }
            : listingPrice ?? marketPrice,
          offers,
          isLocalSale: listings > 0 && stock > 0,
          stock,
          listings,
        };
      }),
    [printingsByCard, suggestions],
  );

  if (printings.length === 0 && suggestionRows.length === 0) {
    return null;
  }

  return (
    <div className={cn('space-y-6', className)}>
      {suggestionRows.length > 0 ? (
        <section className="overflow-hidden rounded-lg border border-brand-700/15 bg-surface shadow-sm">
          <div className="border-b border-brand-700/10 px-3 py-2.5 sm:px-4">
            <h2 className="font-heading text-heading-sm text-brand-950 sm:text-heading-md">
              {t('list.search.recommendations')}
            </h2>
            {query ? (
              <p className="mt-0.5 font-sans text-xs text-brand-700 sm:text-sm">
                {t('list.search.recommendationHint', { query })}
              </p>
            ) : null}
          </div>

          {displayMode === 'grid' ? (
            <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {suggestionRows.map((row) => {
                const card = row.suggestion.card;
                const variantInfo = getVariantInfo(row.bestVariant);
                const detailUrl = row.bestPrinting
                  ? getPrintingDetailUrl(row.bestPrinting)
                  : getSuggestionDetailUrl(row.suggestion, row.bestVariant);
                const displayPrice = formatDualPrice(row.displayPrice);

                return (
                  <article
                    key={`${card.id}-${row.bestVariant?.id ?? 'suggestion'}`}
                    onClick={() => {
                      if (detailUrl) navigate(detailUrl);
                    }}
                    onKeyDown={(event) => {
                      if (!detailUrl) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(detailUrl);
                      }
                    }}
                    role={detailUrl ? 'button' : undefined}
                    tabIndex={detailUrl ? 0 : undefined}
                    className={GRID_CARD_CLASS}
                  >
                    <div className={GRID_MEDIA_CLASS}>
                      <CatalogImage
                        src={null}
                        alt={card.name}
                        className={GRID_IMAGE_CLASS}
                      />
                    </div>
                    <div className="flex flex-1 flex-col gap-1.5 p-2.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant={row.isLocalSale ? 'success' : 'neutral'}>
                          {row.isLocalSale
                            ? catalogSaleCountLabel(t, row.stock)
                            : t('stock.outOfStock')}
                        </Badge>
                        {row.listings > 0 ? (
                          <Badge variant="brand">
                            {t('labels.listingsCount', { count: row.listings })}
                          </Badge>
                        ) : null}
                        {!row.isLocalSale ? (
                          <Badge variant="brand">{t('stock.informative')}</Badge>
                        ) : null}
                      </div>
                      <div>
                        <h3 className="line-clamp-2 font-heading text-sm font-semibold text-brand-950">
                          {card.name}
                        </h3>
                        <p className="mt-1 line-clamp-2 font-sans text-xs text-brand-700">
                          {[card.type, variantInfo].filter(Boolean).join(' / ')}
                        </p>
                      </div>
                      <PriceBlock
                        className="mt-auto"
                        amount={displayPrice?.amount ?? t('list.priceUnavailable')}
                        secondary={
                          displayPrice?.secondary ??
                          (row.isLocalSale ? t('price.storeFrom') : t('price.marketReference'))
                        }
                        size="sm"
                      />
                      {!row.isLocalSale ? (
                        <p className="rounded-md bg-brand-100/60 px-2 py-1 font-sans text-xs text-brand-700">
                          {t('offers.noProductsForCode')}
                        </p>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="divide-y divide-brand-700/10">
              {suggestionRows.map((row) => {
                const card = row.suggestion.card;
                const variantInfo = getVariantInfo(row.bestVariant);
                const detailUrl = row.bestPrinting
                  ? getPrintingDetailUrl(row.bestPrinting)
                  : getSuggestionDetailUrl(row.suggestion, row.bestVariant);

                return (
                  <article
                    key={`${card.id}-${row.bestVariant?.id ?? 'suggestion'}`}
                    onClick={() => {
                      if (detailUrl) navigate(detailUrl);
                    }}
                    onKeyDown={(event) => {
                      if (!detailUrl) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(detailUrl);
                      }
                    }}
                    role={detailUrl ? 'button' : undefined}
                    tabIndex={detailUrl ? 0 : undefined}
                    className="grid cursor-pointer grid-cols-[72px_minmax(0,1fr)] gap-3 px-3 py-2.5 transition-colors hover:bg-brand-100/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 sm:grid-cols-[88px_minmax(0,1fr)_210px] sm:px-4"
                  >
                    <div className="h-32 w-[88px] overflow-hidden rounded-md bg-brand-100 sm:h-40 sm:w-[108px]">
                      <CatalogImage
                        src={null}
                        alt={card.name}
                        className="h-full w-full object-contain"
                      />
                    </div>

                    <div className="min-w-0">
                      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                        <Badge variant={row.isLocalSale ? 'success' : 'neutral'}>
                          {row.isLocalSale
                            ? catalogSaleCountLabel(t, row.stock)
                            : t('stock.outOfStock')}
                        </Badge>
                        <Badge variant="brand">
                          {row.isLocalSale ? t('labels.listingsCount', { count: row.listings }) : t('stock.informative')}
                        </Badge>
                      </div>

                      <h3 className="line-clamp-1 font-heading text-sm font-semibold text-brand-950">
                        {card.name}
                      </h3>
                      <p className="mt-0.5 line-clamp-1 font-sans text-xs text-brand-700">
                        {[card.type, variantInfo].filter(Boolean).join(' / ')}
                      </p>
                    </div>

                    <div className="col-span-2 flex flex-col gap-1.5 sm:col-span-1 sm:items-end">
                      {(() => {
                        const displayPrice = formatDualPrice(row.displayPrice);
                        return (
                          <PriceBlock
                            className="sm:text-right"
                            amount={displayPrice?.amount ?? t('list.priceUnavailable')}
                            secondary={
                              displayPrice?.secondary ??
                              (row.isLocalSale ? t('price.storeFrom') : t('price.marketReference'))
                            }
                            size="sm"
                          />
                        );
                      })()}
                      {!detailUrl ? (
                        <p className="max-w-[16rem] font-sans text-xs text-brand-700 sm:text-right">
                          {t('offers.noneInformative')}
                        </p>
                      ) : null}
                    </div>

                    <div className="col-span-2 sm:col-start-2 sm:col-span-2">
                      {row.offers.length > 0 ? (
                        <OfferList offers={row.offers} />
                      ) : (
                        <p className="mt-2 rounded-md bg-brand-100/60 px-2.5 py-1.5 font-sans text-xs text-brand-700">
                          {t('offers.noProductsForCode')}
                        </p>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      ) : null}

      {printings.length > 0 ? (
        <section>
          <div className="mb-3">
            <h2 className="font-heading text-heading-sm text-brand-950 sm:text-heading-md">
              {t('list.search.marketplaceResults')}
            </h2>
            <p className="mt-0.5 font-sans text-xs text-brand-700 sm:text-sm">
              {t('offers.marketplaceHint')}
            </p>
          </div>

          {displayMode === 'grid' ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {printings.map((printing, index) => {
                const stock = getStock(printing);
                const listingCount = getListingCount(printing);
                const isInStock = printing.availability.isAvailable && stock > 0;
                const printingInfo = getPrintingInfo(printing);
                const listingPrice = getListingPrice(printing);
                const marketPrice = getMarketPriceFromPrinting(printing);
                const priceToShow = listingPrice ?? marketPrice;
                const displayPrice = formatDualPrice(priceToShow);
                const detailUrl = getPrintingDetailUrl(printing);
                const offers = getOfferRows([printing]);

                return (
                  <article
                    key={`${printing.card.id}-${printing.setCode ?? ''}-${printing.id}-${index}`}
                    onClick={() => navigate(detailUrl)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(detailUrl);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className={GRID_CARD_CLASS}
                  >
                    <div className={GRID_MEDIA_CLASS}>
                      <CatalogImage
                        src={null}
                        alt={printing.card.name}
                        className={GRID_IMAGE_CLASS}
                      />
                    </div>
                    <div className="flex flex-1 flex-col gap-1.5 p-2.5">
                      <div>
                        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                          <Badge variant={isInStock ? 'success' : 'neutral'}>
                            {isInStock
                              ? catalogSaleCountLabel(t, stock)
                              : t('stock.outOfStock')}
                          </Badge>
                          {listingCount > 0 ? (
                            <Badge variant="brand">
                              {t('labels.listingsCount', { count: listingCount })}
                            </Badge>
                          ) : null}
                        </div>
                        <h3 className="line-clamp-2 font-heading text-sm font-semibold text-brand-950">
                          {printing.card.name}
                        </h3>
                        {printingInfo ? (
                          <p className="mt-0.5 line-clamp-2 font-sans text-xs text-brand-700">
                            {printingInfo}
                          </p>
                        ) : null}
                      </div>

                      <PriceBlock
                        className="mt-auto"
                        amount={displayPrice?.amount ?? t('list.priceUnavailable')}
                        secondary={displayPrice?.secondary ?? (listingPrice ? t('price.storeFrom') : t('price.marketReference'))}
                        size="sm"
                      />

                      {offers.length === 0 ? (
                        <p className="rounded-md bg-brand-100/60 px-2 py-1 font-sans text-xs text-brand-700">
                          {t('offers.noProductsForCode')}
                        </p>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="space-y-3">
              {printings.map((printing, index) => {
                const stock = getStock(printing);
                const listingCount = getListingCount(printing);
                const isInStock = printing.availability.isAvailable && stock > 0;
                const printingInfo = getPrintingInfo(printing);
                const listingPrice = getListingPrice(printing);
                const marketPrice = getMarketPriceFromPrinting(printing);
                const priceToShow = listingPrice ?? marketPrice;
                const displayPrice = formatDualPrice(priceToShow);
                const detailUrl = getPrintingDetailUrl(printing);
                const offers = getOfferRows([printing]);

                return (
                  <article
                    key={`${printing.card.id}-${printing.setCode ?? ''}-${printing.id}-${index}`}
                    onClick={() => navigate(detailUrl)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(detailUrl);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    className="grid cursor-pointer gap-3 rounded-lg border border-brand-700/15 bg-surface p-2.5 shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 md:grid-cols-[132px_minmax(0,1fr)_minmax(280px,0.9fr)]"
                  >
                    <div className="mx-auto h-44 w-[118px] overflow-hidden rounded-md bg-brand-100 md:mx-0">
                      <CatalogImage
                        src={null}
                        alt={printing.card.name}
                        className="h-full w-full object-contain"
                      />
                    </div>

                    <div className="min-w-0">
                      <div className="mb-2 flex flex-wrap items-center gap-1.5">
                        <Badge variant={isInStock ? 'success' : 'neutral'}>
                          {isInStock
                            ? catalogSaleCountLabel(t, stock)
                            : t('stock.outOfStock')}
                        </Badge>
                        <Badge variant="brand">
                          {listingCount > 0
                            ? t('labels.listingsCount', { count: listingCount })
                            : t('stock.informative')}
                        </Badge>
                      </div>
                      <h3 className="font-heading text-sm font-semibold text-brand-950">
                        {printing.card.name}
                      </h3>
                      {printingInfo ? (
                        <p className="mt-0.5 font-sans text-xs text-brand-700">{printingInfo}</p>
                      ) : null}
                      <PriceBlock
                        className="mt-2"
                        amount={displayPrice?.amount ?? t('list.priceUnavailable')}
                        secondary={displayPrice?.secondary ?? (listingPrice ? t('price.storeFrom') : t('price.marketReference'))}
                        size="sm"
                      />
                    </div>

                    <div className="min-w-0">
                      {offers.length > 0 ? (
                        <OfferList offers={offers} />
                      ) : (
                        <p className="rounded-md bg-brand-100/60 px-2.5 py-1.5 font-sans text-xs text-brand-700">
                          {t('offers.noProductsForCode')}
                        </p>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
